import React, { useEffect, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { fetchManagerToday, type ManagerTodayData } from "../../../liff/api.js";
import {
  MobileShell, Card, CardHeader, MetricGrid, ProgressBar, SeverityPill, MobileSpinner, EmptyMobile,
} from "../../../liff/components/Mobile.js";

export function ManagerTodayPlaceholderPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<ManagerTodayData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    fetchManagerToday(session.tenant_id, session.session_token)
      .then(setData)
      .catch((e: unknown) => setError((e as Error).message));
  }, [session]);

  if (error) return (
    <MobileShell title="無法載入今日總覽"><Card><p className="text-sm text-danger">{error}</p></Card></MobileShell>
  );
  if (!data) return <MobileSpinner />;
  const avgPct = Math.round(data.totals.completion_rate_avg * 100);

  return (
    <MobileShell title="今日總覽" subtitle={`${data.date_local} · 跨案場`}>
      <Card>
        <CardHeader title="平均完成率" hint={`${data.totals.sites_active} 個案場`} />
        <p className="text-3xl font-semibold mb-3">{avgPct}<span className="text-base text-text-secondary">%</span></p>
        <ProgressBar value={data.totals.completion_rate_avg} />
      </Card>

      <MetricGrid items={[
        { label: "在勤案場", value: String(data.totals.sites_active), tone: "default" },
        { label: "漏巡", value: String(data.totals.overdue_count), tone: data.totals.overdue_count > 0 ? "danger" : "success" },
        { label: "今日事件", value: String(data.totals.incidents_count), tone: "warning" },
        { label: "未結案", value: String(data.totals.open_incidents_count), tone: data.totals.open_incidents_count > 0 ? "danger" : "success" },
      ]} />

      <Card>
        <CardHeader title="案場狀態" hint={`${data.sites.length} 站`} />
        <ul className="space-y-2">
          {data.sites.map((s) => {
            const pct = Math.round(s.completion_rate * 100);
            const riskColor = s.risk === "low" ? "text-success" : s.risk === "med" ? "text-warning" : "text-danger";
            return (
              <li key={s.site_id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <p className="text-sm font-medium truncate">{s.site_name}</p>
                  <span className={`text-[10px] font-semibold uppercase ${riskColor}`}>{s.risk}</span>
                </div>
                <div className="flex items-center gap-3 text-[11px] text-text-muted mb-1.5">
                  <span>{pct}%</span>
                  {s.overdue_count > 0 && <span className="text-danger">漏 {s.overdue_count}</span>}
                  {s.incidents_count > 0 && <span className="text-warning">事件 {s.incidents_count}</span>}
                </div>
                <ProgressBar value={s.completion_rate} />
              </li>
            );
          })}
        </ul>
      </Card>

      <Card>
        <CardHeader title="重點事件" hint={`Top ${data.top_incidents.length}`} />
        {data.top_incidents.length === 0 ? <EmptyMobile title="今天沒有重點事件 🎉" /> : (
          <ul className="space-y-2">
            {data.top_incidents.map((i) => (
              <li key={i.id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-sm font-medium truncate">{i.title}</p>
                  <SeverityPill severity={i.severity} />
                </div>
                <p className="text-[11px] text-text-muted mt-1 truncate">{i.site_name} · {i.occurred_at_local} · {i.status === "open" ? "待處理" : i.status === "ack" ? "已確認" : "已解決"}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card>
        <CardHeader title="待處理" hint={`${data.pending_actions.length} 項`} />
        {data.pending_actions.length === 0 ? <EmptyMobile title="待處理事項已清空 🎉" /> : (
          <ul className="divide-y divide-border-subtle -my-2">
            {data.pending_actions.map((a) => (
              <li key={a.id} className="py-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm">{a.label}</p>
                  {a.due_at_local && <p className="text-[11px] text-text-muted">截止 {a.due_at_local}</p>}
                </div>
                {a.route_to && (
                  <a href={a.route_to} className="text-[11px] text-accent shrink-0">前往 →</a>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </MobileShell>
  );
}
